import * as echarts from 'echarts';
import { find, flatMap, round, sum, sumBy, uniq } from 'lodash';

import { CONVERSION_FROM_FIRST } from '@src/emp-3.0/components/Sankey/utils';
import { IFunnel } from '@src/emp-3.0/types';
import { COLORS } from '@src/emp-3.0/utils/constants';
import { parseNumToPercentage } from '@src/emp-3.0/utils/number';

type EChartsOption = echarts.EChartsOption;
type BarLabelOption = NonNullable<echarts.BarSeriesOption['label']>;

function needsVerticalLabel(funnelData: IFunnel[], screenWidth: number): boolean {
  const breakdownCount = funnelData.length - 1;
  const stepKeys = uniq(
    flatMap(funnelData, (obj) => Object.keys(obj).filter((key) => key.startsWith('funnel_step'))),
  );
  const stepCount = stepKeys.length;
  return stepCount > 5 && breakdownCount > 5 && screenWidth < 2560;
}

const generateLabelOption = (
  neededVerticalLabels: boolean = false,
  option?: BarLabelOption,
): BarLabelOption => {
  let labelOption: BarLabelOption = {
    show: true,
    formatter: (params) => {
      const { value } = params;
      return typeof value === 'number' ? round(value * 100, 2) + '' : value.toString();
    },
    position: 'top',
    color: 'rgba(0, 0, 0, 0.45)',
    ...option,
  };
  if (neededVerticalLabels) {
    labelOption = {
      ...labelOption,
      position: ['50%', '0%'],
      rotate: 90,
      offset: [10, -6],
    };
  }
  return labelOption;
};

const labelOptionForSankey: BarLabelOption = {
  show: true,
  formatter: (params) => {
    const { componentIndex, data } = params;
    return parseNumToPercentage(data[componentIndex + 1], 2);
  },
  position: 'top',
};

function calculateFunnelStepSum(funnel_data: IFunnel[] = [], field: string) {
  const firstTotal =
    funnel_data.length > 0
      ? sumBy(Object.keys(funnel_data[0]), (key) => {
          if (key.startsWith('funnel_step_')) {
            return funnel_data[0][key];
          }
          return 0;
        })
      : 0;
  return funnel_data.map((item, index) => {
    const stepKeys = Object.keys(item).filter((key) => key.startsWith('funnel_step_'));
    const total = sum(stepKeys.map((key) => item[key]));
    let percentageOfFirstGroup: string | number =
      firstTotal === 0 || total === 0 ? 0 : (total / firstTotal) * 100;
    percentageOfFirstGroup = Number.isInteger(percentageOfFirstGroup)
      ? percentageOfFirstGroup
      : percentageOfFirstGroup.toFixed(2);
    percentageOfFirstGroup += '%';
    return { group: index, total, percentageOfFirstGroup, field: item[field] ?? 'Overall' };
  });
}

export const convertFunnelDataToBarChartDataForFunnel = ({
  field,
  funnelRes,
  axisLabelWidth = 200,
  option,
}: {
  field: string;
  funnelRes: {
    event_names: string[];
    funnel_data: IFunnel[];
  };
  axisLabelWidth?: number;
  option?: EChartsOption;
}) => {
  const { event_names, funnel_data } = funnelRes;
  const funnelStepSum = calculateFunnelStepSum(funnel_data, field);
  const needVerticalLabel = needsVerticalLabel(funnel_data, window.screen.width);

  const xAxisData = event_names.map((name, index) => `Step ${index + 1}: ${name}`);

  const series = funnel_data
    .map((funnel) => {
      if (funnel[field] !== undefined || !field) {
        const data = event_names.map((_, index) => {
          const step = `step_${index + 1}`;
          const count = funnel[step].count;
          const conversionFromFirst = funnel[step][CONVERSION_FROM_FIRST];
          if (count === 0) {
            return 0;
          } else {
            return conversionFromFirst || conversionFromFirst === 0 ? conversionFromFirst : 1;
          }
        });
        return {
          name: field ? funnel[field] : 'Overall',
          type: 'bar',
          label: generateLabelOption(needVerticalLabel),
          emphasis: {
            focus: 'series',
          },
          data,
          barMinWidth: 32,
        };
      }
      return;
    })
    .filter(Boolean);

  return {
    grid: {
      left: 40,
      right: 0,
      bottom: 100,
      containLabel: true,
    },
    legend: {
      show: true,
      bottom: 10,
      formatter: (name) => {
        const foundItem = find(funnelStepSum, { field: name });
        if (foundItem) {
          return `${name} - ${foundItem.percentageOfFirstGroup}`;
        }
        return name;
      },
    },
    xAxis: [
      {
        type: 'category',
        position: 'bottom',
        axisLabel: {
          interval: 0,
          color: 'rgba(0, 0, 0, 0.88)',
          fontWeight: 500,
          fontSize: 14,
          width: axisLabelWidth,
          overflow: 'breakAll',
        },
        axisLine: {
          lineStyle: {
            color: '#F0F0F0',
          },
        },
        axisTick: {
          show: false,
        },
        data: xAxisData,
      },
    ],
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          formatter: function (value) {
            return round(value * 100) + '%';
          },
        },
        splitLine: {
          lineStyle: {
            color: '#F0F0F0',
          },
        },
      },
    ],
    series,
    ...option,
  };
};

export const convertFunnelDataToBarChartDataForSankey = (
  funnelRes: {
    event_names: string[];
    funnel_data: IFunnel[];
  },
  option?: EChartsOption,
) => {
  const { event_names, funnel_data } = funnelRes;
  const dimensions = ['step', CONVERSION_FROM_FIRST];
  const source = event_names.map((_, index) => [`step_${index + 1}`]);
  const series = [
    {
      name: 'CONVERSION_FROM_FIRST',
      type: 'bar',
      label: labelOptionForSankey,
      emphasis: {
        focus: 'series',
      },
      itemStyle: {
        color: (params) => {
          const { dataIndex } = params;
          return (option?.color ?? COLORS)[dataIndex];
        },
      },
    },
  ];

  funnel_data.forEach((item) => {
    source.forEach((arr) => {
      const step = arr[0];
      arr.push(item[step][CONVERSION_FROM_FIRST] ?? 1);
    });
  });

  return {
    dataset: {
      dimensions,
      source,
    },
    xAxis: [
      {
        type: 'category',
        position: 'top',
        axisLabel: {
          show: true,
          margin: 30,
          color: 'rgba(0, 0, 0, 0.88)',
          fontWeight: 500,
          fontSize: 14,
          formatter: function (value) {
            // 'step_1' to 'Step 1', ...
            const parts = value.split('_');
            return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        show: false,
      },
    ],
    series,
    ...option,
  };
};
